import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const PHRASES = [
  "Ciao! Mi stavi cercando?",
  "Sbircio dal cassetto…",
  "Hai letto qualcosa di bello oggi?",
  "Psst… c'è un nuovo articolo?",
  "Noi giraffe vediamo lontano!",
  "Curiosa come sempre, io.",
  "Torno nel cassetto, a presto!",
];

const Giraffe = () => (
  <svg width="96" height="150" viewBox="0 0 120 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* Neck */}
    <path d="M48 96 C45 130 43 165 42 200 L82 200 C80 165 77 130 73 96 Z" fill="#D79A66" stroke="#1C1917" strokeWidth="3" strokeLinejoin="round" />
    {/* Neck spots */}
    <ellipse cx="54" cy="128" rx="7" ry="8" fill="#9F583F" />
    <ellipse cx="70" cy="150" rx="7" ry="8" fill="#9F583F" />
    <ellipse cx="52" cy="172" rx="6" ry="7" fill="#9F583F" />
    <ellipse cx="70" cy="118" rx="5" ry="6" fill="#9F583F" />
    {/* Ossicones */}
    <line x1="49" y1="60" x2="47" y2="40" stroke="#1C1917" strokeWidth="3" strokeLinecap="round" />
    <line x1="71" y1="60" x2="73" y2="40" stroke="#1C1917" strokeWidth="3" strokeLinecap="round" />
    <circle cx="46" cy="37" r="6" fill="#9F583F" stroke="#1C1917" strokeWidth="3" />
    <circle cx="74" cy="37" r="6" fill="#9F583F" stroke="#1C1917" strokeWidth="3" />
    {/* Ears */}
    <ellipse cx="34" cy="70" rx="10" ry="6" fill="#D79A66" stroke="#1C1917" strokeWidth="3" transform="rotate(-25 34 70)" />
    <ellipse cx="86" cy="70" rx="10" ry="6" fill="#D79A66" stroke="#1C1917" strokeWidth="3" transform="rotate(25 86 70)" />
    {/* Head */}
    <ellipse cx="60" cy="82" rx="25" ry="27" fill="#D79A66" stroke="#1C1917" strokeWidth="3" />
    {/* Muzzle */}
    <ellipse cx="60" cy="98" rx="17" ry="13" fill="#EAD9C6" stroke="#1C1917" strokeWidth="2.5" />
    {/* Nostrils */}
    <circle cx="54" cy="98" r="1.6" fill="#1C1917" />
    <circle cx="66" cy="98" r="1.6" fill="#1C1917" />
    {/* Smile */}
    <path d="M54 104 Q60 108 66 104" stroke="#1C1917" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* Eyes */}
    <circle cx="51" cy="80" r="4.5" fill="#1C1917" />
    <circle cx="69" cy="80" r="4.5" fill="#1C1917" />
    <circle cx="52.4" cy="78.6" r="1.5" fill="#fff" />
    <circle cx="70.4" cy="78.6" r="1.5" fill="#fff" />
    {/* Head spot */}
    <ellipse cx="72" cy="72" rx="5" ry="6" fill="#9F583F" opacity="0.85" />
  </svg>
);

export const GiraffePeek = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [side, setSide] = useState("left");
  const [phrase, setPhrase] = useState(null);
  const timers = useRef([]);

  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/accedi");

  useEffect(() => {
    if (hidden) return;
    const clearAll = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };

    const hide = () => {
      setVisible(false);
      setPhrase(null);
      // schedule next appearance in 25-55s
      const next = 25000 + Math.random() * 30000;
      timers.current.push(setTimeout(appear, next));
    };

    const appear = () => {
      setSide(Math.random() > 0.5 ? "left" : "right");
      setPhrase(null);
      setVisible(true);
      // auto-retreat after 6.5s unless interacted
      timers.current.push(setTimeout(hide, 6500));
    };

    // first appearance shortly after load
    timers.current.push(setTimeout(appear, 6000));
    return clearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  const onPoke = () => {
    setPhrase(PHRASES[Math.floor(Math.random() * PHRASES.length)]);
    // keep her around a bit longer to read the phrase
    timers.current.push(
      setTimeout(() => {
        setVisible(false);
        setPhrase(null);
        const next = 25000 + Math.random() * 30000;
        timers.current.push(setTimeout(() => setVisible(true), next));
      }, 4500)
    );
  };

  if (hidden) return null;

  const isLeft = side === "left";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="giraffe"
          data-testid="giraffe-peek"
          initial={{ x: isLeft ? -130 : 130, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: isLeft ? -130 : 130, opacity: 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 14 }}
          className={`fixed z-40 select-none ${isLeft ? "left-0 bottom-28" : "right-0 bottom-44"}`}
        >
          <div className="relative">
            <AnimatePresence>
              {phrase && (
                <motion.div
                  key="bubble"
                  initial={{ opacity: 0, y: 8, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  data-testid="giraffe-speech"
                  className={`absolute -top-14 ${isLeft ? "left-16" : "right-16"} whitespace-nowrap bg-white border border-[#E7E5E4] shadow-md px-4 py-2 font-body text-sm text-[#1C1917] rounded-2xl`}
                >
                  {phrase}
                  <span className={`absolute -bottom-1.5 ${isLeft ? "left-5" : "right-5"} w-3 h-3 bg-white border-b border-r border-[#E7E5E4] rotate-45`} />
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              onClick={onPoke}
              whileHover={{ y: -6 }}
              whileTap={{ scale: 0.92, rotate: isLeft ? -4 : 4 }}
              aria-label="Saluta la giraffa"
              data-testid="giraffe-button"
              className="block cursor-pointer bg-transparent border-0 p-0"
              style={{ transform: isLeft ? "none" : "scaleX(-1)" }}
            >
              <Giraffe />
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
